/**
 * CURSIVE SIGNATURE SELECTOR
 * 
 * Allows users to select from 5 different cursive signature styles.
 * The selected signature is used on cover letters and handwritten declarations.
 * 
 * Signature Styles:
 * 1. Elegant Script - Classic flowing cursive
 * 2. Modern Signature - Contemporary casual style
 * 3. Bold Cursive - Strong, confident strokes
 * 4. Delicate Script - Light, feminine touch
 * 5. Professional Hand - Business-like cursive
 */

import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Check,
  PenTool,
  Type,
  Sparkles,
  RefreshCw,
  Download,
  ChevronRight
} from 'lucide-react';

// Signature font styles - using Google Fonts web-safe cursive alternatives
const SIGNATURE_STYLES = [
  {
    id: 1,
    name: 'Elegant Script',
    description: 'Classic flowing cursive with graceful loops',
    fontFamily: "'Dancing Script', cursive",
    fontSize: '32px',
    color: '#1a1a2e',
    preview: 'Elegant & Timeless'
  },
  {
    id: 2,
    name: 'Modern Signature',
    description: 'Contemporary casual style',
    fontFamily: "'Pacifico', cursive",
    fontSize: '30px',
    color: '#1a1a2e',
    preview: 'Modern & Fresh'
  },
  {
    id: 3,
    name: 'Bold Cursive',
    description: 'Strong, confident strokes',
    fontFamily: "'Satisfy', cursive",
    fontSize: '34px',
    color: '#1a1a2e',
    preview: 'Bold & Confident'
  },
  {
    id: 4,
    name: 'Delicate Script',
    description: 'Light, elegant touch',
    fontFamily: "'Great Vibes', cursive",
    fontSize: '36px',
    color: '#1a1a2e',
    preview: 'Delicate & Refined'
  },
  {
    id: 5,
    name: 'Professional Hand',
    description: 'Business-like cursive signature',
    fontFamily: "'Allura', cursive",
    fontSize: '34px',
    color: '#1a1a2e',
    preview: 'Professional & Clean'
  }
];

export default function CursiveSignatureSelector({
  clientName = '',
  selectedStyle = 1,
  onStyleChange,
  onSignatureGenerated,
  onComplete
}) {
  const [currentStyle, setCurrentStyle] = useState(selectedStyle);
  const [signatureName, setSignatureName] = useState(clientName);
  const [generating, setGenerating] = useState(false);
  const [previewSignature, setPreviewSignature] = useState(null);
  const canvasRef = useRef(null);

  // Load Google Fonts for signatures
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;700&family=Pacifico&family=Satisfy&family=Great+Vibes&family=Allura&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  // Update signature name when clientName prop changes
  useEffect(() => {
    if (clientName && !signatureName) {
      setSignatureName(clientName);
    }
  }, [clientName]);

  // Format name for signature (First Letter Caps, rest lowercase)
  const formatSignatureName = (name) => {
    if (!name) return '';
    return name.split(' ').map(word => {
      if (word.length === 0) return '';
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }).join(' ');
  };

  // Generate signature preview on canvas
  const generateSignatureCanvas = (styleId, name) => {
    const style = SIGNATURE_STYLES.find(s => s.id === styleId);
    if (!style || !name) return null;

    // Format name properly (First letter caps, rest lowercase)
    const formattedName = formatSignatureName(name);

    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 100;
    const ctx = canvas.getContext('2d');

    // Clear canvas with transparent background
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Set font and style
    ctx.font = `${style.fontSize} ${style.fontFamily.replace(/'/g, '')}`;
    ctx.fillStyle = style.color;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';  // Changed to left align

    // Draw the signature - moved left
    ctx.fillText(formattedName, 20, canvas.height / 2);

    return canvas.toDataURL('image/png');
  };

  // Handle style selection
  const handleStyleSelect = (styleId) => {
    setCurrentStyle(styleId);
    if (onStyleChange) {
      onStyleChange(styleId);
    }
    
    // Generate preview
    if (signatureName) {
      const preview = generateSignatureCanvas(styleId, signatureName);
      setPreviewSignature(preview);
    }
  };

  // Generate final signature
  const handleGenerateSignature = () => {
    if (!signatureName.trim()) {
      toast.error('Please enter a name for the signature');
      return;
    }

    setGenerating(true);
    
    // Format the name properly
    const formattedName = formatSignatureName(signatureName);
    
    // Small delay for visual feedback
    setTimeout(() => {
      const signatureData = generateSignatureCanvas(currentStyle, formattedName);
      setPreviewSignature(signatureData);
      
      if (onSignatureGenerated) {
        onSignatureGenerated({
          styleId: currentStyle,
          styleName: SIGNATURE_STYLES.find(s => s.id === currentStyle)?.name,
          name: formattedName,
          imageData: signatureData
        });
      }
      
      setGenerating(false);
      toast.success('Signature generated successfully!');
    }, 500);
  };

  // Handle continue
  const handleContinue = () => {
    if (!previewSignature) {
      handleGenerateSignature();
    }
    
    if (onComplete) {
      onComplete({
        styleId: currentStyle,
        styleName: SIGNATURE_STYLES.find(s => s.id === currentStyle)?.name,
        name: signatureName,
        imageData: previewSignature
      });
    }
  };

  const selectedStyleData = SIGNATURE_STYLES.find(s => s.id === currentStyle);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-white flex items-center justify-center gap-3">
          <PenTool className="w-7 h-7 text-amber-400" />
          Select Your Signature Style
        </h2>
        <p className="text-zinc-400 mt-2">
          Choose from 5 professional cursive signatures for your dispute letters
        </p>
      </div>

      {/* Name Input */}
      <Card className="bg-zinc-900 border-zinc-700">
        <CardContent className="pt-6">
          <div className="space-y-2">
            <Label className="text-zinc-300">Your Full Name (as it will appear)</Label>
            <Input
              value={signatureName}
              onChange={(e) => setSignatureName(e.target.value)}
              placeholder="Enter your full legal name"
              className="bg-zinc-800 border-zinc-600 text-white text-lg"
              data-testid="signature-name-input"
            />
            <p className="text-xs text-zinc-500">
              This name will be used on your cover letter and handwritten declaration
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Signature Style Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {SIGNATURE_STYLES.map((style) => (
          <Card
            key={style.id}
            data-testid={`signature-style-${style.id}`}
            className={`cursor-pointer transition-all duration-200 ${
              currentStyle === style.id
                ? 'bg-amber-900/30 border-amber-500 ring-2 ring-amber-500/50'
                : 'bg-zinc-900 border-zinc-700 hover:border-zinc-500'
            }`}
            onClick={() => handleStyleSelect(style.id)}
          >
            <CardContent className="pt-4 pb-4">
              {/* Selection indicator */}
              <div className="flex items-center justify-between mb-3">
                <Badge 
                  variant={currentStyle === style.id ? "default" : "outline"}
                  className={currentStyle === style.id ? "bg-amber-600" : ""}
                >
                  Style {style.id}
                </Badge>
                {currentStyle === style.id && (
                  <Check className="w-5 h-5 text-amber-400" />
                )}
              </div>
              
              {/* Signature Preview */}
              <div 
                className="h-16 flex items-center justify-center bg-white rounded-lg mb-3 overflow-hidden"
              >
                <span
                  style={{
                    fontFamily: style.fontFamily,
                    fontSize: style.fontSize,
                    color: style.color,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {signatureName || 'Your Name'}
                </span>
              </div>
              
              {/* Style Info */}
              <div>
                <h3 className="font-semibold text-white">{style.name}</h3>
                <p className="text-xs text-zinc-400">{style.description}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Generated Signature Preview */}
      {previewSignature && (
        <Card className="bg-gradient-to-r from-green-900/20 to-zinc-900 border-green-500/30">
          <CardHeader>
            <CardTitle className="text-green-400 flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Your Signature Preview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-white rounded-lg p-6 flex items-center justify-center">
              <img 
                src={previewSignature} 
                alt="Signature Preview" 
                className="max-h-24"
                data-testid="signature-preview-image"
              />
            </div>
            <p className="text-xs text-zinc-500 mt-3 text-center">
              Using: {selectedStyleData?.name} • This signature will appear on your dispute documents
            </p>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 justify-between">
        <Button
          variant="outline"
          onClick={handleGenerateSignature}
          disabled={generating || !signatureName.trim()}
          className="border-zinc-600"
          data-testid="generate-signature-btn"
        >
          {generating ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Type className="w-4 h-4 mr-2" />
              Preview Signature
            </>
          )}
        </Button>
        
        <Button
          onClick={handleContinue}
          disabled={!signatureName.trim()}
          className="bg-green-600 hover:bg-green-700"
          data-testid="signature-continue-btn"
        >
          Continue
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>

      {/* Instructions */}
      <Card className="bg-zinc-800/50 border-zinc-700">
        <CardContent className="pt-4">
          <div className="text-sm text-zinc-400 space-y-2">
            <p className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-amber-600 text-white text-xs flex items-center justify-center">1</span>
              Enter your full legal name exactly as you want it to appear
            </p>
            <p className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-amber-600 text-white text-xs flex items-center justify-center">2</span>
              Click on a signature style to preview how your name looks
            </p>
            <p className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-amber-600 text-white text-xs flex items-center justify-center">3</span>
              Your signature will be added to the cover letter and declaration
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
